import RouteTemplate from 'ember-route-template'
import type WLVocabularyOverusedWordsAnalyserRoute from 'teacher-ui-client/reporting/ui-scope/writing-legends/vocabulary/overused-words-analyser/route'
import type WLVocabularyOverusedWordsAnalyserController from 'teacher-ui-client/reporting/ui-scope/writing-legends/vocabulary/overused-words-analyser/controller'
import {
  AverageWordUsage,
  AverageDeviationAlerts,
  VocabularyAnalyserChart,
} from 'teacher-ui-client/components/writing-legends/charts/vocabulary-analyser-chart'
import OverusedWords from 'teacher-ui-client/components/writing-legends/overused-words'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { trackedFunction } from 'reactiveweb/function'
import { toRequestScope } from 'teacher-ui-client/services/reporting'
import { Product } from 'teacher-ui-client/utils/products'
import { use } from 'ember-resources'
import { keepLatest } from 'reactiveweb/keep-latest'
import { t } from 'ember-intl'
import FidgetSpinnerWaveComponent from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { ErrorsUnexpected } from 'teacher-ui-client/components/errors/unexpected'
import NoDataMessage from 'teacher-ui-client/components/no-data-message'

export interface OverusedWordsAnalyserData {
  overused_words_stats: {
    students: {
      student_id: string
      student_blake_id: number
      school_blake_id: number
      rate: number
      first_name: string
      last_name: string
      grade_position: number
      grade_position_adjusted: number // this field is generated by the backend for supported grade positions (2 to 8)
    }[]
    trendline: {
      grade_position: number
      rate: number
    }[]
  }
  class_grade_averages: {
    grade_position: number
    rate: number
  }[]
  terrific_alternatives: string[]
}

interface Signature {
  Args: {
    model: ModelFor<WLVocabularyOverusedWordsAnalyserRoute>
    controller: WLVocabularyOverusedWordsAnalyserController
  }
}

export class WLVocabularyOverusedWordsAnalyserPage extends Component<Signature> {
  @service reporting!: ReportingService

  fetchData = trackedFunction(this, () => {
    if (this.args.controller.word === '') return null

    const requestScope = toRequestScope(this.args.model.uiScope)

    const url = this.reporting.buildWritingLegendsUrl({
      product: Product.WL,
      requestScope,
      reportName: 'vocabulary/overused-words-analyser',
    })

    const query = this.reporting.buildQuery({
      period: this.args.model.period,
    })

    query.word = this.args.controller.word

    return this.reporting.fetchReport<OverusedWordsAnalyserData>(url, query)
  })

  @use
  latestData = keepLatest({
    value: () => this.fetchData.value,
    when: () => this.fetchData.isPending,
  })

  get terrificAlternatives() {
    return this.latestData?.terrific_alternatives.join(', ')
  }

  get isUnsupportedScope() {
    const requestScope = toRequestScope(this.args.model.uiScope)
    return requestScope.scope !== 'school-class'
  }

  <template>
    {{#if this.isUnsupportedScope}}
      <NoDataMessage
        @compact={{true}}
        @body={{t "components.noDataMessage.limitedScope" scope="class" htmlSafe=true}}
        class="m-4"
      />
    {{else}}
      <OverusedWords @uiScope={{@model.uiScope}} class="m-4" />

      {{#if this.latestData}}
        <VocabularyAnalyserChart
          class="h-96"
          @higherIsBetter={{false}}
          @yAxisTitle="Frequency"
          @trendline={{this.latestData.overused_words_stats.trendline}}
          @studentsData={{this.latestData.overused_words_stats.students}}
          @classGradeAverages={{this.latestData.class_grade_averages}}
        />

        <div class="mx-auto w-2/3 space-y-10">
          <AverageDeviationAlerts
            @higherIsBetter={{false}}
            @word={{@controller.word}}
            @trendline={{this.latestData.overused_words_stats.trendline}}
            @studentsData={{this.latestData.overused_words_stats.students}}
            @classGradeAverages={{this.latestData.class_grade_averages}}
          />

          <div class="text-center">
            <h3>{{t "pages.wlVocabulary.terrificAlternatives" word=@controller.word}}</h3>
            <p>{{this.terrificAlternatives}}</p>
          </div>

          <AverageWordUsage
            @message={{t "pages.wlVocabulary.studentsWithHighWordUsage" word=@controller.word}}
            @studentsData={{this.latestData.overused_words_stats.students}}
            @classGradeAverages={{this.latestData.class_grade_averages}}
            @showAboveAverage={{true}}
          />
        </div>
      {{else if this.fetchData.isPending}}
        <FidgetSpinnerWaveComponent @centered={{true}} />
      {{else if this.fetchData.isError}}
        <ErrorsUnexpected class="m-4" data-test-usage-error />
      {{else}}
        <NoDataMessage class="m-4" data-test-usage-empty />
      {{/if}}
    {{/if}}
  </template>
}

export default RouteTemplate(WLVocabularyOverusedWordsAnalyserPage)
